
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Table from "@/domain/restaurant/Table";
import DialogBanner from "@/components/app/DialogBanner.vue";
import ConfirmationDialog from "@/components/alert/ConfirmationDialog.vue";
import FormService from "@/services/FormService";
import { VuetifyForm } from "@/plugins/vuetify";
import RemoteServices from "@/services/RemoteServices";
import Room from "@/domain/restaurant/Room";
@Component({
  components: { ConfirmationDialog, DialogBanner }
})
export default class TableDialog extends Vue {
  @Prop({ default: false })
  value!: boolean;

  @Prop({
    default: () => {
      return new Table();
    }
  })
  table!: Table;

  @Prop({ default: null })
  room!: Room | null;

  tableCopy = Table.copy(this.table);

  formValid = false;

  requiredString = [FormService.requiredStringRule];

  positiveNumber = [
    FormService.requiredNumberRule,
    FormService.positiveNumberRule
  ];

  isJoinedTable = false;

  tableAvailability = Table.tableAvailability;

  tablePreference = Table.tablePreference;

  tableAvailabilityHints = Table.tableAvailabilityHints();

  tableComponentIds: number[] = [];

  mounted() {
    this.resetJoinedTable();
  }

  get joinedTable() {
    return this.isJoinedTable;
  }

  set joinedTable(newVal) {
    this.isJoinedTable = newVal;
    if (newVal) {
      this.tableCopy.tableName = this.$tc("table.name.autogenerated");
    } else this.tableCopy.tableName = "";
  }

  get filteredTableComponentList() {
    return this.room!.tables.filter(
      component => component.joinedTableComponents.length === 0
    );
  }

  get dialog() {
    return this.value;
  }

  set dialog(newValue) {
    this.$emit("input", newValue);
    if (!newValue) {
      this.resetCopy();
      this.tableForm.resetValidation();
      this.resetJoinedTable();
    }
  }

  @Watch("table")
  resetCopy() {
    this.tableCopy = Table.copy(this.table);
    this.resetJoinedTable();
  }

  resetJoinedTable() {
    this.isJoinedTable =
      this.table.tableId != null &&
      this.table.joinedTableComponents.length !== 0;
  }

  get newTable() {
    return this.table.tableId == null;
  }

  get tableForm() {
    return this.$refs.tableForm as VuetifyForm;
  }

  get hasChanges() {
    return Table.haveDifferences(this.tableCopy, this.table);
  }

  getBannerTitle() {
    if (this.newTable) return this.$t("table.create.message");
    else return this.$t("table.edit.message");
  }

  /* Prevents changes being detected by same list in different order */
  sortComponentList() {
    this.tableCopy.joinedTableComponents.sort(
      Table.TablesByNameAscendingComparator
    );
  }

  async createTable() {
    await this.tableForm.validate();
    if (this.formValid) {
      this.$store.dispatch("loading");
      try {
        this.tableCopy.roomId = this.room!.roomId;
        if (this.isJoinedTable) {
          Table.getComponentMockToSend(this.tableCopy);
          this.tableCopy = await RemoteServices.createJoinedTable(
            this.tableCopy
          );
        } else {
          this.tableCopy = await RemoteServices.createTable(this.tableCopy);
        }
        this.$store.dispatch("success", this.$t("table.create.success"));
        this.addTable();
      } catch (error) {
        this.tableCopy.joinedTableComponents = [];
        this.$store.dispatch("error", error.message);
      }
      this.$store.dispatch("clearLoading");
    }
  }

  get confirmationDialog() {
    return this.$refs.confirmationDialog as ConfirmationDialog;
  }

  async deleteTable() {
    if (
      await this.confirmationDialog.open(
        this.$tc("table.delete.message"),
        this.$t("delete.confirmation") +
          "\n" +
          this.table.tableName! +
          "\n" +
          this.$t("delete.warning")
      )
    ) {
      this.$store.dispatch("loading");
      try {
        await RemoteServices.deleteTable(this.tableCopy.tableId);
        this.$store.dispatch("success", this.$t("table.delete.success"));
        this.removeTable();
      } catch (error) {
        this.$store.dispatch("error", error.message);
      }
      this.$store.dispatch("clearLoading");
    }
  }

  addTable() {
    this.$emit("new-table", this.tableCopy);
    this.dialog = false;
  }

  removeTable() {
    this.$emit("delete-table", this.tableCopy);
    this.dialog = false;
  }

  updateTargetTable() {
    Table.updateTable(this.table, this.tableCopy);
    this.dialog = false;
  }

  async updateTable() {
    await this.tableForm.validate();
    if (this.formValid) {
      this.$store.dispatch("loading");
      try {
        if (this.isJoinedTable) {
          this.tableCopy = await RemoteServices.updateJoinedTable(
            Table.getCopyWithChangedValues(this.table, this.tableCopy)
          );
        } else {
          this.tableCopy = await RemoteServices.updateTable(
            Table.getCopyWithChangedValues(this.table, this.tableCopy)
          );
        }
        this.$store.dispatch("success", this.$t("table.edit.success"));
        this.updateTargetTable();
      } catch (error) {
        this.$store.dispatch("error", error.message);
      }
      this.$store.dispatch("clearLoading");
    }
  }

  get isAdmin() {
    return this.$store.getters.isAdmin;
  }
}
